import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { appelNatifEnCours, hautParleurNatif, suivreLeSonNatif } from './appelNatif';

/**
 * Le haut-parleur pendant un appel.
 *
 * Rien en JavaScript ne sait router le son d'iOS : ni la balise `<audio>`,
 * ni WebRTC, ni `setSinkId`, qui n'existe pas la-bas. C'est
 * `AVAudioSession` qui decide, et elle vit dans `AppDelegate.swift`.
 *
 * Sur le web, il n'y a rien a router — le navigateur sort par la sortie du
 * systeme. L'appel se fait donc sans bruit, et le bouton ne s'affiche pas.
 */
/** Ce que la session porte a un instant donne. */
export interface EtatDuSon {
  hautParleur: boolean;
  route: string;
}

interface HautParleurPlugin {
  regler(options: { actif: boolean }): Promise<{ hautParleur: boolean; route: string }>;
  /** Fin d'appel : le natif rend la session a WebKit. */
  relacher(): Promise<void>;
  addListener(
    event: 'etat',
    callback: (etat: EtatDuSon) => void,
  ): Promise<PluginListenerHandle>;
}

const greffon = registerPlugin<HautParleurPlugin>('HautParleur');

/** Le bouton n'a de sens que la ou le son se route. */
export function hautParleurDisponible(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Pose la route et rend ce qui a ete obtenu — pas ce qui a ete demande.
 * Un refus silencieux a couté deux tentatives : il n'y en aura plus.
 */
export async function reglerHautParleur(
  actif: boolean,
): Promise<{ obtenu: boolean; route: string } | null> {
  if (!hautParleurDisponible()) return null;
  /*
    Un appel porte par l'application se route de son cote, et sans lutte :
    WebKit n'ouvre alors aucune capture, donc plus personne ne repose de
    route derriere nous. Le greffon ci-dessous ne sert plus qu'aux appels
    video, restes sur le chemin de la vue web.
  */
  if (appelNatifEnCours()) return hautParleurNatif(actif);
  const reponse = await greffon.regler({ actif });
  return { obtenu: Boolean(reponse?.hautParleur), route: reponse?.route ?? '?' };
}

/**
 * A la fin de l'appel. Sans cela, le natif continuerait de reposer sa
 * route a chaque branchement d'ecouteur, longtemps apres qu'on a
 * raccroche.
 */
export async function relacherHautParleur(): Promise<void> {
  if (!hautParleurDisponible()) return;
  await greffon.relacher();
}

/**
 * Suit la session pendant tout l'appel, et pas seulement a l'instant du
 * reglage.
 *
 * C'est la difference entre savoir ce qu'on a demande et savoir ce qu'on
 * a obtenu : WebKit repose sa configuration apres coup, et c'est
 * justement l'apres qu'on ne voyait pas. Le natif pousse desormais
 * chaque changement, avec sa raison.
 *
 * Rend de quoi se desabonner, ou `null` sur le web ou il n'y a rien a
 * suivre.
 */
export function suivreLeSon(
  ecoute: (etat: EtatDuSon) => void,
): Promise<PluginListenerHandle> | null {
  if (!hautParleurDisponible()) return null;
  // Les deux chemins parlent la meme langue, et un seul est actif a la
  // fois : on ecoute les deux plutot que de deviner lequel repondra.
  const web = greffon.addListener('etat', ecoute);
  const natif = suivreLeSonNatif(ecoute);
  return Promise.all([web, natif]).then(([a, b]) => ({
    remove: async () => {
      await a.remove();
      await b.remove();
    },
  }));
}
